import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Button, Flex, message, Space, Typography } from 'antd';
import { AppstoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { deviceHistoryPath, devicePath } from '../../routes';
import './DevicePage.scss';
import CreateModalDevice, { DeviceFormValues } from '../../components/utils/CreateModalDevice/CreateModalDevice';
import DeviceGeneralTable, { GeneralDevice } from '../../components/general/DevicesGeneralTable';
import { useTranslation } from '../../i18n/i18n';
import { deviceApi } from '../../api/device';
import {
  Device,
  DeviceDriverType,
  DeviceSensorUltrasonicModel,
  DeviceType,
} from '../../gen/proto/api/gateway/v1/device_pb';
import useAuthStore from '../../stores/auth.store';
import { deviceLocationTypeLabel } from '../../utils/mapTranslationEnums';
import useTenantStore, { Tenant } from '../../stores/tenant.store';
import { DeviceTrackerType } from '../../gen/proto/api/gateway/v1/device_tracker_pb';
import { ADAPTIVE_TABLE_CONTAINER_CLASS, useAdaptivePageSize } from '../../hooks/useAdaptivePageSize';

const { Title } = Typography;
const FIRST_PAGE = 1;

function toGeneralDevice(device: Device, locationType: DeviceTrackerType, tenants: Tenant[]): GeneralDevice {
  let model = '';
  let sensorType: number | undefined;
  let sewerLocation: number | undefined;
  let ultrasonicModel: number | undefined;
  let driverModel: number | undefined;

  if (device.device.case === 'driver') {
    model = DeviceDriverType[device.device.value.model] ?? '';
    driverModel = device.device.value.model;
  } else if (device.device.case === 'sensor') {
    sensorType = device.device.value.type;
    sewerLocation = device.device.value.sewerLocation;
    const sensorModel = device.device.value.model;
    if (sensorModel.case === 'ultrasonicModel') {
      model = DeviceSensorUltrasonicModel[sensorModel.value] ?? '';
      ultrasonicModel = sensorModel.value;
    }
  }

  return {
    uuid: device.uuid,
    name: device.name,
    type: DeviceType[device.type] ?? '',
    fleet: model,
    location: deviceLocationTypeLabel(locationType),
    connect: false,
    connected_at: '',
    sensorType,
    sewerLocation,
    ultrasonicModel,
    driverModel,
    tenant: tenants.find((t) => t.value === device.tenantUuid)?.label ?? device.tenantUuid,
    tenantUuid: device.tenantUuid,
  };
}

function DevicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation('devices');

  const [isCreateDeviceModalOpen, setIsCreateDeviceModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<GeneralDevice | null>(null);
  const [deviceList, setDeviceList] = useState<GeneralDevice[]>([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [page, setPage] = useState(1);
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const tenants = useTenantStore((state) => state.tenants);
  const { pageSize, containerRef, containerHeight, recalculate } = useAdaptivePageSize();

  useEffect(() => {
    setPage(FIRST_PAGE);
  }, [selectedTenant, pageSize]);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await deviceApi.getListByPage(page, pageSize);
      const locations = await Promise.all(
        response.devices.map((d) => deviceApi.getCurrentLocation(d.uuid).catch(() => null)),
      );
      setDeviceList(
        response.devices.map((d, i) =>
          toGeneralDevice(d, locations[i]?.movementType ?? DeviceTrackerType.UNSPECIFIED, tenants),
        ),
      );
      setTotalDevices(response.metadata?.totalCount ?? 0);
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, messageApi, t, tenants]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices, selectedTenant]);

  useLayoutEffect(() => {
    recalculate();
  }, [totalDevices, recalculate]);

  function onSeeHistory(uuid: string) {
    const path = location.pathname.split('/').slice(0, 2).join('/');
    navigate(`${path}/${devicePath}/${uuid}/${deviceHistoryPath}`);
  }

  function onOpenEditDevice(record: GeneralDevice) {
    setSelectedDevice(record);
    setIsCreateDeviceModalOpen(true);
  }

  async function onCreateDevice(values: DeviceFormValues) {
    const sensor =
      values.type === DeviceType.SENSOR && values.sensorType && values.sewerLocation && values.ultrasonicModel
        ? { type: values.sensorType, sewerLocation: values.sewerLocation, ultrasonicModel: values.ultrasonicModel }
        : undefined;

    const driver =
      values.type === DeviceType.DRIVER && values.driverModel ? { model: values.driverModel } : undefined;

    await deviceApi.create(values.name, values.type, sensor, driver, values.tenantUuid);
    setIsCreateDeviceModalOpen(false);
    await fetchDevices();
  }

  async function onDeleteDevice(uuid: string) {
    try {
      await deviceApi.deleteDevice(uuid);
      if (deviceList.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        await fetchDevices();
      }
    } catch (error) {
      messageApi.error(t('error_deleting'));
    }
  }

  async function onEditDevice(values: DeviceFormValues) {
    const sensor =
      values.type === DeviceType.SENSOR && values.sensorType && values.sewerLocation && values.ultrasonicModel
        ? { type: values.sensorType, sewerLocation: values.sewerLocation, ultrasonicModel: values.ultrasonicModel }
        : undefined;

    const driver =
      values.type === DeviceType.DRIVER && values.driverModel ? { model: values.driverModel } : undefined;

    const editedDevice = await deviceApi.updateDevice(
      selectedDevice!.uuid,
      values.name,
      values.type,
      sensor,
      driver,
      values.tenantUuid,
    );

    const updatedGeneralDevice = toGeneralDevice(editedDevice, DeviceTrackerType.UNSPECIFIED, tenants);
    setDeviceList((prev) => prev.map((d) => (d.uuid === selectedDevice!.uuid ? updatedGeneralDevice : d)));

    setSelectedDevice(null);
    setIsCreateDeviceModalOpen(false);
    await fetchDevices();
  }

  return (
    <Flex vertical>
      {contextHolder}
      {isSuperAdminOrCompanyAdmin && (
        <CreateModalDevice
          open={isCreateDeviceModalOpen}
          onCancel={() => {
            setIsCreateDeviceModalOpen(false);
            setSelectedDevice(null);
          }}
          handleSubmit={onCreateDevice}
          handleEdit={onEditDevice}
          mode={selectedDevice ? 'edit' : 'create'}
          initialValues={
            selectedDevice
              ? {
                  name: selectedDevice.name,
                  type: DeviceType[selectedDevice.type as keyof typeof DeviceType],
                  sensorType: selectedDevice.sensorType,
                  sewerLocation: selectedDevice.sewerLocation,
                  ultrasonicModel: selectedDevice.ultrasonicModel,
                  driverModel: selectedDevice.driverModel,
                  tenantUuid: selectedDevice.tenantUuid,
                }
              : undefined
          }
        />
      )}
      <br />
      <Space className={'DevicePage__row'}>
        <Space size={'middle'}>
          <AppstoreOutlined className={'DevicePage__icon'} />
          <Title level={1} className={'DevicePage__title'}>
            {t('title')}
          </Title>
        </Space>
        {isSuperAdminOrCompanyAdmin && (
          <div className={'DevicePage__buttons'}>
            <Button type={'primary'} onClick={() => setIsCreateDeviceModalOpen(true)}>
              <PlusOutlined />
              {t('create')}
            </Button>
          </div>
        )}
      </Space>
      <br />
      <div
        className={`DevicePage__table ${ADAPTIVE_TABLE_CONTAINER_CLASS}`}
        style={{ height: containerHeight }}
        ref={containerRef}
      >
        <DeviceGeneralTable
          data={deviceList}
          loading={loading}
          onDeleteDevice={onDeleteDevice}
          onSeeHistory={onSeeHistory}
          onEditDevice={onOpenEditDevice}
          pagination={{
            pageSize,
            total: totalDevices,
            current: page,
            showSizeChanger: false,
          }}
          onChange={(pagination) => setPage(pagination.current ?? 1)}
        />
      </div>
    </Flex>
  );
}

export default DevicePage;
